import { useState } from 'react';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent!', {
        description: "We'll get back to you soon."
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error sending message', {
        description: 'Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="py-16 bg-[#F9F7F2]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl animate-fade-in-up">
            <span className="text-[#8B5E3C] text-sm font-medium uppercase tracking-wider">Get In Touch</span>
            <h1 className="font-manrope text-4xl sm:text-5xl font-bold text-[#1A1A1A] mt-4 mb-6">
              Contact Kitchen99
            </h1>
            <p className="text-lg text-[#4B5563]">
              Have questions about our cabinets or need help with your order? Our friendly team is here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="animate-fade-in-up">
                <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-6">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                      <Phone size={24} className="text-[#8B5E3C]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A1A1A]">Phone</p>
                      <a href="tel:+1-647-550-1313" className="text-[#6B7280] hover:text-[#8B5E3C] transition-colors">
                        +1-647-550-1313
                      </a>
                      <p className="text-sm text-[#9CA3AF]">Available Mon-Fri</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                      <Mail size={24} className="text-[#8B5E3C]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A1A1A]">Email</p>
                      <a href="mailto:kitchen99.ca@gmail.com" className="text-[#6B7280] hover:text-[#8B5E3C] transition-colors">
                        kitchen99.ca@gmail.com
                      </a>
                      <p className="text-sm text-[#9CA3AF]">We respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} className="text-[#8B5E3C]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A1A1A]">Address</p>
                      <p className="text-[#6B7280]">
                        Toronto, ON, Canada
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                      <Clock size={24} className="text-[#8B5E3C]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A1A1A]">Business Hours</p>
                      <p className="text-[#6B7280]">
                        Monday - Friday: 9:00 AM - 6:00 PM<br />
                        Saturday: 10:00 AM - 4:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="h-full flex items-center justify-center" data-testid="contact-success">
                  <div className="text-center animate-scale-in">
                    <CheckCircle size={64} className="mx-auto text-[#10B981] mb-6" />
                    <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-4">
                      Message Sent!
                    </h2>
                    <p className="text-[#6B7280] mb-6">
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </p>
                    <Button onClick={() => setSubmitted(false)}>
                      Send Another Message
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-[#F9F7F2] p-8 animate-fade-in-up">
                  <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-6">
                    Send Us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Full Name *</label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Smith"
                          required
                          className="bg-white"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <label className="form-label">Email Address *</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="bg-white"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Phone Number</label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(416) 555-0123"
                          className="bg-white"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <label className="form-label">Subject *</label>
                        <Input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="How can we help?"
                          required
                          className="bg-white"
                          data-testid="contact-subject-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">Message *</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us more about your inquiry..."
                        rows={6}
                        required
                        className="bg-white"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="bg-[#1A1A1A] hover:bg-[#333333]"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2" size={18} />
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-96 bg-[#E5E7EB]" data-testid="contact-map">
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <MapPin size={48} className="mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#6B7280]">Interactive map coming soon</p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
